/**
 * ABM Categorías — Fase 8
 *
 * CRUD del catálogo de categorías laborales (código + descripción).
 * Soft delete via flag `activa`. Admin general only (matriz M14).
 */

import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import { Add, Edit, ToggleOn, ToggleOff } from '@mui/icons-material';

import {
  useCategorias,
  useCreateCategoria,
  useUpdateCategoria,
  type Categoria,
} from '../../../hooks/useCatalogos';
import { zTextRequerido, zTextOpcional } from '../../../utils/validations';

const schema = z.object({
  codigo: zTextOpcional({ label: 'Código', max: 20 }),
  descripcion: zTextRequerido({ label: 'Descripción', min: 2, max: 200 }),
  activa: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

export default function CategoriasList() {
  const { data: categorias = [], isLoading, isError, error } = useCategorias();
  const create = useCreateCategoria();
  const update = useUpdateCategoria();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Categoria | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { codigo: '', descripcion: '', activa: true },
  });

  const openCreate = () => {
    setEditing(null);
    form.reset({ codigo: '', descripcion: '', activa: true });
    setDialogOpen(true);
  };

  const openEdit = (c: Categoria) => {
    setEditing(c);
    form.reset({
      codigo: c.codigo ?? '',
      descripcion: c.descripcion,
      activa: c.activa,
    });
    setDialogOpen(true);
  };

  const handleToggle = async (c: Categoria) => {
    await update.mutateAsync({ id: c.id, payload: { activa: !c.activa } });
  };

  const onSubmit = async (values: FormValues) => {
    const payload = {
      codigo: values.codigo ? String(values.codigo) : null,
      descripcion: values.descripcion,
      activa: values.activa,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      setDialogOpen(false);
    } catch {
      // se muestra en el alert
    }
  };

  const apiError = (editing ? update.error : create.error) as Error | null;

  const columns = useMemo<GridColDef<Categoria>[]>(
    () => [
      {
        field: 'codigo',
        headerName: 'Código',
        width: 110,
        renderCell: (p) => (
          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
            {p.row.codigo ?? '—'}
          </Typography>
        ),
      },
      { field: 'descripcion', headerName: 'Descripción', flex: 1, minWidth: 250 },
      {
        field: 'activa',
        headerName: 'Estado',
        width: 110,
        renderCell: (p: GridRenderCellParams<Categoria>) => (
          <Chip
            label={p.row.activa ? 'Activa' : 'Inactiva'}
            size="small"
            color={p.row.activa ? 'success' : 'default'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (p: GridRenderCellParams<Categoria>) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => openEdit(p.row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={p.row.activa ? 'Desactivar' : 'Activar'}>
              <IconButton
                size="small"
                color={p.row.activa ? 'warning' : 'success'}
                onClick={() => handleToggle(p.row)}
              >
                {p.row.activa ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Categorías
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Categorías laborales del convenio (código + descripción).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nueva categoría
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar: {(error as Error)?.message ?? 'desconocido'}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={categorias}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{ border: 0 }}
          localeText={{ noRowsLabel: 'No hay categorías cargadas' }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <DialogTitle>{editing ? 'Editar categoría' : 'Nueva categoría'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {apiError && <Alert severity="error">{apiError.message}</Alert>}

              <TextField
                {...form.register('codigo')}
                fullWidth
                label="Código"
                placeholder="Ej: A, B, AUX..."
                error={!!form.formState.errors.codigo}
                helperText={form.formState.errors.codigo?.message ?? 'Opcional, máximo 20 caracteres'}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
              <TextField
                {...form.register('descripcion')}
                required
                fullWidth
                label="Descripción"
                error={!!form.formState.errors.descripcion}
                helperText={form.formState.errors.descripcion?.message}
              />
              <FormControlLabel
                control={
                  <Switch
                    {...form.register('activa')}
                    defaultChecked={form.getValues('activa')}
                  />
                }
                label="Activa"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={form.formState.isSubmitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={form.formState.isSubmitting}
              startIcon={
                form.formState.isSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              {editing ? 'Guardar cambios' : 'Crear categoría'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
